import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Un camp de text bilingüe, escrit des de l'idioma en què treballes.
 *
 * Fins al 06/08/2026 aquest component ensenyava sempre els dos idiomes un
 * sobre l'altre, amb **l'anglès marcat com a obligatori** i el català com una
 * «traducció pendent». Era al revés del que diu l'aplicació des del 05/08:
 * l'idioma és el català i l'anglès és contingut opcional que ja arribarà. A la
 * pràctica, qui creava una missió en català havia d'omplir primer l'anglès per
 * no veure avisos de camp incomplet.
 *
 * Ara s'escriu **en l'idioma de la interfície**, i l'altre és un camp plegat
 * que s'obre si el vols. `required` val per a l'idioma en què s'escriu, no per
 * a l'anglès.
 */
function MultilingualTextInput({
  value = {},
  onChange,
  label,
  placeholder = {},
  required = false,
  maxLength = null,
  className = ""
}) {
  const { t, i18n } = useTranslation();

  // Xarxa de seguretat: si arriba una cadena en comptes d'un objecte {en, ca},
  // escampar-la amb ...value la convertiria en els seus caràcters indexats per
  // posició i això és el que va destruir el contingut d'una hipòtesi a
  // producció. La causa està arreglada a l'origen (la pantalla d'edició demana
  // el JSON sense traduir), però una segona barrera aquí no fa cap mal.
  const segur = value && typeof value === 'object' && !Array.isArray(value) ? value : {};

  const idioma = (i18n.language || '').startsWith('en') ? 'en' : 'ca';
  const altre = idioma === 'en' ? 'ca' : 'en';
  const nomAltre = t(`multilingual.${altre === 'en' ? 'english' : 'catalan'}`);

  const teAltre = Boolean((segur[altre] || '').trim());
  const [obert, setObert] = useState(false);
  useEffect(() => {
    if (teAltre) setObert(true);
  }, [teAltre]);

  const escriu = (lang, nou) => onChange({ ...segur, [lang]: nou });

  return (
    <div className={`camp-idioma ${className}`}>
      <label className="camp-idioma-etiqueta">
        {label} {required && <span className="required">*</span>}
      </label>

      <input
        type="text"
        value={segur[idioma] || ''}
        onChange={(e) => escriu(idioma, e.target.value)}
        placeholder={placeholder[idioma] || ''}
        maxLength={maxLength}
        required={required}
        className="camp-idioma-camp"
      />
      {maxLength && (
        <div className="char-count">{(segur[idioma] || '').length}/{maxLength}</div>
      )}

      <details className="camp-idioma-altre" open={obert} onToggle={(e) => setObert(e.target.open)}>
        <summary>
          {teAltre
            ? t('multilingual.otherHas', { lang: nomAltre })
            : t('multilingual.otherAdd', { lang: nomAltre })}
        </summary>
        <input
          type="text"
          value={segur[altre] || ''}
          onChange={(e) => escriu(altre, e.target.value)}
          placeholder={placeholder[altre] || ''}
          maxLength={maxLength}
          className="camp-idioma-camp"
        />
        {maxLength && (
          <div className="char-count">{(segur[altre] || '').length}/{maxLength}</div>
        )}
      </details>
    </div>
  );
}

export default MultilingualTextInput;
